
































































import { AuthErrorEvent } from '@/components/organisms/dialog/error-dialog/events'
import CrossPageTransition from '@/mixin/cross-page-transition'
import Step from '@spa/components/atoms/Step.vue'
import { MailMagazineLoadError } from '@spa/components/pages/auth/error/mail-magazine-load-error'
import { MailMagazineSubscribeError } from '@spa/components/pages/auth/error/mail-magazine-subscribe-error'
import { toContextQuery } from '@spa/components/pages/paid-membership/subscribe/interaction/context'
import _ from 'lodash'
import { Mixins } from 'vue-mixin-decorator'
import { Component, Emit, Prop } from 'vue-property-decorator'

import { log } from '@/log'
import { RawTag } from '@/models/api/tags'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import * as CurrentTime from '@/models/current-time/functions'
import { SmsAuthService } from '@/services/auth/sms-auth'
import { SessionDataService } from '@/services/session/session-data-service'
import { UserService } from '@ajax/modules/services/user'
import MailMagazine from '@spa/components/organisms/mail-magazine/MailMagazine.vue'
import { Decorator, Decorators } from '@spa/components/organisms/mail-magazine/model'
import { AlreadyUsedTelError } from '@spa/components/pages/auth/error/already-used-tel-error'
import { ExceedSmsCodeAuthTrialTimesError } from '@spa/components/pages/auth/error/exceed-sms-code-auth-trial-times-error'
import { SignupError } from '@spa/components/pages/auth/error/signup-error'
import { loadingFormParts, SignUpFormParts } from '@spa/components/pages/auth/signup/shared/form-parts/form-parts'
import { createSignUpFormParts } from '@spa/components/pages/auth/signup/shared/form-parts/form-parts-factory'
import { SignupWithSmsAuthInteraction } from '@spa/components/pages/auth/signup/with-sms/model/interaction'
import {
  DefaultSmsAuthRepository,
  SmsAuthRepository,
} from '@spa/components/pages/auth/signup/with-sms/model/sms-auth-repository'
import { Status } from '@spa/mixins/loading'
import { PluginMixin } from '@spa/mixins/plugin'
import { RegistrationPayload } from '@spa/store/modules/user/actions'
import { createRegistrationPayload } from '@spa/store/modules/user/functions'
import { UserState } from '@spa/store/modules/user/types'
import CannotUseTheTel from './components/CannotUseTheTel.vue'
import Complete from './components/Complete.vue'
import InputEmail from './components/InputEmail.vue'
import InputPassword from './components/InputPassword.vue'
import InputSmsAuthToken, { ResendStatus } from './components/InputSmsAuthToken.vue'
import InputTel from './components/InputTel.vue'

@Component({
  components: {
    Step,
    Complete,
    InputTel,
    InputSmsAuthToken,
    CannotUseTheTel,
    InputEmail,
    InputPassword,
    MailMagazine,
  },
})
export default class SignupWithSmsAuth
    extends Mixins<PluginMixin & CrossPageTransition>(PluginMixin, CrossPageTransition) {

  @Prop({ default: '/s/mypage' })
  /**
   * @see SignupPageProps.next
   */
  readonly next: string

  @Prop({ default: '/login' })
  /**
   * @see SignupPageProps.loginLink
   */
  readonly loginLink: string

  readonly currentTime = CurrentTime.create()
  readonly behaviorConfig = new BehaviorConfig()

  readonly userService: UserService = new UserService()
  readonly smsAuthRepository: SmsAuthRepository = new DefaultSmsAuthRepository(
      tel => this.$store.dispatch('user/update', { tel }),
      new SmsAuthService()
  )

  status: Status = Status.Pending
  tokenResendStatus: ResendStatus = 'standby'

  formParts: SignUpFormParts = loadingFormParts

  interaction: SignupWithSmsAuthInteraction = SignupWithSmsAuthInteraction.initialize()

  mailMagazines: RawTag[] = []

  private readonly unknownErrorMsg = 'エラーが発生しました。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'

  get nextPath(): string {
    return `${this.next}?${toContextQuery(this.interaction.context.incrementStep())}`
  }

  async created() {
    this.status = Status.Loading

    if (await SessionDataService.userIsRegistered()) {
      this.goTo(this.next)

      return
    }

    this.formParts = createSignUpFormParts(this.behaviorConfig)
    await this.$store.dispatch('user/get')

    this.status = Status.Pending
  }

  async submitTel(tel: string) {
    this.status = Status.Loading
    try {
      await this.smsAuthRepository.startSmsAuthBy(tel)

      this.interaction.inputTel(tel)

      this.status = Status.Pending
    } catch (e) {
      // 「登録済みエラー」の場合は、throwせず、状態を更新して画面遷移
      if (e instanceof AlreadyUsedTelError) {
        this.interaction.cannotUseThePhoneNumber()
        this.status = Status.Failed
      }

      // それ以外のエラーの場合はそのままthrow
      this.handleError(e)
    }
  }

  retry() {
    this.interaction = SignupWithSmsAuthInteraction.initialize()
  }

  async resendToken() {
    this.tokenResendStatus = 'sending'
    try {
      await this.smsAuthRepository.resendToken()
      this.tokenResendStatus = 'complete'
      // tslint:disable-next-line:no-magic-numbers
      setTimeout(() => { this.tokenResendStatus = 'standby' }, 2000)
    } catch (e) {
      this.tokenResendStatus = 'standby'
      this.handleError(e)
    }
  }

  async submitToken(token: string) {
    this.status = Status.Loading

    try {
      await this.smsAuthRepository.authenticate(token)

      this.interaction.succeedSMSAuth()
      this.status = Status.Pending
    } catch (e) {
      this.handleError(e)
    }
  }

  inputEmail(email: string) {
    this.interaction.inputEmail(email)
  }

  inputPassword(password: string) {
    this.interaction.inputPassword(password)
  }

  async register(password: string) {
    this.interaction.inputPassword(password)

    try {
      if (!this.interaction.isCompleteInput()) {
        const modalMessage = [
          this.interaction.tel.fold('', () => '電話番号が未入力です'),
          this.interaction.isTelAuthenticated ? '' : '電話番号が未認証です',
          this.interaction.email.fold('', () => 'メールアドレスが未入力です'),
          this.interaction.password.fold('', () => 'パスワードが未入力です'),
          '初めから登録をやり直してください。',
        ].filter(s => s.length > 0).join('<br>')

        throw new SignupError(
          'interaction state must be completed',
          modalMessage
        )
      }

      const registeredAt = this.currentTime.now.toJSON()

      const payload: RegistrationPayload = createRegistrationPayload(
        {
          username: this.interaction.email.value,
          password: this.interaction.password.value,
          // Email is also passed because username is email at present.
          email: this.interaction.email.value,
          is_delivery: true,
          registered_at: registeredAt,
        },
        this.mailMagazines
      )

      await this.$store.dispatch('user/register', payload)

      const state: UserState = this.$store.state.user
      const error = state.error.register

      if (error) {
        this.interaction.failRegistration()

        // 再度メールアドレス入力ステップに戻すので、FailedではなくPendingとする
        this.status = Status.Pending

        return
      }

      const mailMagazineSubscribeError = state.error.mailMagazine
      if (mailMagazineSubscribeError) {
        throw new MailMagazineSubscribeError(
          mailMagazineSubscribeError.message,
          `
          会員登録は成功しましたが、メールマガジン登録に失敗しました。<br/>
          メールマガジンは、アカウントサービスから再登録できます。
          `
        )
      }

      this.interaction.succeedRegister()
      this.status = Status.Completed
    } catch (e) {
      this.handleError(e)
    }
  }

  get decorator(): Decorator {
    return Decorators.subscribeAll
  }

  get shouldShowInputPasswordView(): boolean {
    return this.interaction.isInputPasswordStep || this.interaction.isCompleteInput()
  }

  get error(): Error {
    return this.$store.state.user.error.register
  }

  get hasDuplicateEmailError(): boolean {
    const cause: string = _.get(this.error, 'response.body.message', '')

    return cause.includes('already exists')
  }

  get hasTimeoutError(): boolean {
    const byTimeout: number | undefined = _.get(this.error, 'timeout')

    return byTimeout !== undefined
  }

  onUpdateMailMagazine(updated: RawTag[]): void {
    this.mailMagazines = updated
  }

  failLoadMailMagazine(e: Error): void {
    this.handleError(
      new MailMagazineLoadError(
        e.message,
        [
          'メールマガジン情報が取得できませんでした。',
          '※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。',
        ].join('<br/>')
      )
    )
  }

  private handleError(e) {
    log.error(e)

    if (e instanceof SignupError) {
      this.emitError({
        content: e.label,
        onCloseModal: () => this.$router.go(0),
      })
    } else if (e instanceof MailMagazineLoadError) {
      this.emitError({
        content: e.label,
      })
    } else if (e instanceof ExceedSmsCodeAuthTrialTimesError) {
      this.emitError({
        content: e.label,
        onCloseModal: () => this.retry(),
      })
    } else if (e instanceof MailMagazineSubscribeError) {
      this.emitError({
        content: e.label,
        onCloseModal: () => this.interaction.succeedRegister(),
      })
    } else {
      this.emitError({
        content: this.unknownErrorMsg,
      })
    }
    this.status = Status.Failed
  }

  @Emit('error')
  private emitError(_event: AuthErrorEvent) {
    // only emit event
  }
}
